import React, { createContext, useContext, useReducer } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  X, 
  CreditCard, 
  Check,
  ArrowRight,
  Package,
  Zap
} from 'lucide-react';

// Cart Context
const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_ITEM':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      };

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };

    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(0, action.payload.quantity) }
            : item
        ).filter(item => item.quantity > 0)
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });

  const addItem = (item) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  };

  const removeItem = (id) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const updateQuantity = (id, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getTotalPrice = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      items: state.items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getTotalPrice,
      getTotalItems
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

// Cart Widget Component
export const CartWidget = ({ onToggle }) => {
  const { getTotalItems, getTotalPrice } = useCart();
  const totalItems = getTotalItems();

  return (
    <Button
      onClick={onToggle}
      className="relative bg-purple-600 hover:bg-purple-700 text-white rounded-full p-3 shadow-lg transform hover:scale-105 transition-all duration-300"
    >
      <ShoppingCart className="w-6 h-6" />
      {totalItems > 0 && (
        <Badge className="absolute -top-2 -right-2 bg-red-500 text-white min-w-[20px] h-5 text-xs animate-pulse">
          {totalItems}
        </Badge>
      )}
    </Button>
  );
};

// Cart Sidebar Component
export const CartSidebar = ({ isOpen, onClose }) => {
  const { items, updateQuantity, removeItem, clearCart, getTotalPrice, getTotalItems } = useCart();
  const totalPrice = getTotalPrice();
  const totalItems = getTotalItems();

  const handleCheckout = () => {
    // Checkout logic here
    alert('Ödeme sayfasına yönlendiriliyorsunuz...');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-purple-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Sepetim</h2>
                <p className="text-sm text-gray-600">{totalItems} ürün</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sepetiniz boş</h3>
              <p className="text-gray-600 mb-6">Harika paketlerimizi keşfedin ve sepete ekleyin!</p>
              <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700">
                Alışverişe Başla
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, index) => (
                <Card 
                  key={item.id} 
                  className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300"
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    animation: 'fadeInRight 0.4s ease-out forwards'
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-4">
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center flex-shrink-0`}>
                        <item.icon className="w-6 h-6 text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900 text-sm">{item.name}</h4>
                            <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                            {item.billing && (
                              <Badge variant="outline" className="mt-2 text-xs">
                                {item.billing === 'monthly' ? 'Aylık' : 'Yıllık'}
                              </Badge>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => removeItem(item.id)}
                            className="h-6 w-6 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center space-x-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="h-6 w-6"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="h-6 w-6"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-lg font-bold text-purple-600">₺{(item.price * item.quantity).toLocaleString()}</p>
                            {item.quantity > 1 && (
                              <p className="text-xs text-gray-500">₺{item.price.toLocaleString()} x {item.quantity}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="space-y-4">
              {/* Price Breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Ara Toplam</span>
                  <span className="font-medium">₺{totalPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">KDV (%18)</span>
                  <span className="font-medium">₺{(totalPrice * 0.18).toLocaleString()}</span>
                </div>
                <div className="border-t border-gray-300 pt-2">
                  <div className="flex justify-between">
                    <span className="text-lg font-bold text-gray-900">Toplam</span>
                    <span className="text-lg font-bold text-purple-600">₺{(totalPrice * 1.18).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <Button 
                  onClick={handleCheckout}
                  className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-lg font-semibold group"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Ödemeye Geç
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={clearCart}
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Sepeti Temizle
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={onClose}
                    className="flex-1"
                  >
                    Alışverişe Devam
                  </Button>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-1 text-xs text-gray-600">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Güvenli Ödeme</span>
                </div>
                <div className="flex items-center space-x-1 text-xs text-gray-600">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Hızlı Teslimat</span>
                </div>
                <div className="flex items-center space-x-1 text-xs text-gray-600">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>7/24 Destek</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </>
  );
};